import { Box, Slider, Typography } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { DayOfWeekSelector } from "@outschool/ui-components-website";
import { Input } from "@outschool/ui-legacy-component-library";
import ldPick from "lodash/pick";
import React from "react";

import { useTimeZone } from "../../../../providers/TimeZoneProvider";
import { Filter, FilterList } from "../Filter";
import { ListingChildFilterProps } from "./ListingFilters";

type WeeklyMeetingsFilterProps = {
  filters: SearchFiltersType;
  onChange?: (filters?: SearchFiltersType) => void;
};

function WeeklyMeetingsFilter({
  filters,
  onChange
}: WeeklyMeetingsFilterProps) {
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\TimeFilter"
  );

  return (
    <Filter sectionTitle={t("Weekly meetings")}>
      <Box flex sx={{ alignItems: "center" }}>
        <Input
          type="number"
          min={1}
          max={7}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.weeklyMeetingsMin ?? 0}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.({
              ...filters,
              weeklyMeetingsMin: parseInt(e.target.value, 10)
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle">
          {t("to")}
        </Typography>
        <Input
          type="number"
          min={1}
          max={7}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.weeklyMeetingsMax ?? 7}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.({
              ...filters,
              weeklyMeetingsMax: parseInt(e.target.value, 10)
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle">
          {t("meetings per week")}
        </Typography>
      </Box>
    </Filter>
  );
}

type DurationWeeksFilterProps = {
  filters: SearchFiltersType;
  onChange?: (filters: SearchFiltersType) => void;
};

function DurationWeeksFilter({ filters, onChange }: DurationWeeksFilterProps) {
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\TimeFilter"
  );

  return (
    <Filter sectionTitle={t("Class duration")}>
      <Box flex sx={{ alignItems: "center" }}>
        <Input
          type="number"
          min={1}
          max={16}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.durationWeeksMin ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.({
              ...filters,
              durationWeeksMin: parseInt(e.target.value, 10)
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle">
          {t("to")}
        </Typography>
        <Input
          type="number"
          min={1}
          max={16}
          sx={{ width: "4em", marginRight: ".5em" }}
          value={filters.durationWeeksMax ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.({
              ...filters,
              durationWeeksMax: parseInt(e.target.value, 10)
            })
          }
        />
        <Typography sx={{ marginRight: ".5em" }} variant="subtitle">
          {t("weeks long")}
        </Typography>
      </Box>
    </Filter>
  );
}

// TODO: Migrate new time of day selector work in next iteration of mobile sbs project
// https://gitlab.com/outschool-eng/outschool/-/blob/master/website/app/client/components/browse/TimeFilters.tsx#L110
function TimeOfDaySelector({ value, onChange, step = 0.5 }: $TSFixMe) {
  const [startAfterTime, endByTime] = value;

  return (
    <Box>
      <Box
        sx={{
          position: "relative"
        }}
      >
        <Box
          flex
          sx={{
            justifyContent: "space-between",
            flexDirection: "row"
          }}
        >
          <Typography variant="inherit">
            {dayjs.utc(startAfterTime * 60 * 60 * 1000).format("LT")}
          </Typography>
          <Typography variant="inherit">
            {dayjs.utc(endByTime * 60 * 60 * 1000).format("LT")}
          </Typography>
        </Box>
        <Box
          sx={{
            marginTop: "1em",
            paddingX: "15px"
          }}
        >
          <Slider
            step={step}
            min={Time.DAY_START_HOUR}
            max={Time.DAY_END_HOUR}
            value={value}
            onChange={(_e, values) => {
              const [startAfterTime, endByTime] = values as [number, number];
              const startChanged = startAfterTime !== value.startAfterTime;
              onChange([
                Math.max(
                  Time.DAY_START_HOUR,
                  startChanged
                    ? Math.min(startAfterTime, endByTime - step)
                    : startAfterTime
                ),
                Math.min(
                  Time.DAY_END_HOUR,
                  startChanged
                    ? endByTime
                    : Math.max(endByTime, startAfterTime + step)
                )
              ]);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function TimeFilter({
  searchFilters,
  onChange
}: ListingChildFilterProps) {
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\TimeFilter"
  );
  const times = ldPick(searchFilters, SearchFilters.TIME_FILTERS);

  if (!times.hasOwnProperty("startAfterTime")) {
    times.startAfterTime = Time.DAY_START_HOUR;
  }
  if (!times.hasOwnProperty("endByTime")) {
    times.endByTime = Time.DAY_END_HOUR;
  }

  const { startAfterTime, endByTime, dow } = times;
  const { timeZone } = useTimeZone();

  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
  const timeRangeValues = [startAfterTime, endByTime].sort((a, b) => a - b);

  return (
    <FilterList>
      <Filter sectionTitle={t("Day of the week")}>
        <Box
          sx={{
            padding: "12px 0px 24px 0px"
          }}
        >
          <DayOfWeekSelector
            // @ts-ignore TS(2322): Type 'InputMaybe<string> | undefined' is not assig... Remove this comment to see the full error message
            value={dow}
            onChange={dow => {
              // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              onChange(
                // @ts-ignore TS(2345): Argument of type 'Partial<SearchFilters> & { dow: ... Remove this comment to see the full error message
                Object.assign({}, times, {
                  dow
                })
              );
            }}
            showGroupSelection={false}
          />
        </Box>
      </Filter>
      <Filter
        sectionTitle={t("Time of day ({{timeZone}})", {
          timeZone: Time.timeZoneName(timeZone)
        })}
      >
        <Box
          sx={{
            padding: "12px 0px 48px 0px"
          }}
        >
          <TimeOfDaySelector
            value={timeRangeValues}
            // @ts-ignore
            onChange={([startAfterTime, endByTime]) => {
              // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              onChange(
                // @ts-ignore TS(2345): Argument of type 'Partial<SearchFilters> & { start... Remove this comment to see the full error message
                Object.assign({}, times, {
                  startAfterTime,
                  endByTime
                })
              );
            }}
          />
        </Box>
      </Filter>
      <Box sx={{ marginBottom: "2em" }}>
        <WeeklyMeetingsFilter filters={searchFilters} onChange={onChange} />
      </Box>
      <Box sx={{ marginBottom: "2em" }}>
        <DurationWeeksFilter filters={searchFilters} onChange={onChange} />
      </Box>
    </FilterList>
  );
}
